import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, FormControl, Grid, IconButton, InputLabel, Select, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Prompt } from 'react-router';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';

import { CREATE, DataAPIContext, UPDATE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import HTTPError from '../components/HTTPError';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import { a11yProps } from '../components/TabPanel';
import UsedByPanel from '../components/UsedByPanel';
import RecordList from '../components/RecordList';
import EditTripItemIcon from '@mui/icons-material/BorderColorOutlined';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import { renderingTypesMenuItems } from './utils';
import { ConfirmationDialogText } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        border: `1px solid ${grey[200]}`,
        borderRadius: '5px',
        marginTop: '-0.25rem',
        [theme.breakpoints.down('sm')]: {
            marginTop: "-3rem"
        },
    },
    gridRoot: {
        overflowY: 'auto',
        height: `calc(${theme.cp.contentHeight} - 17.75rem - 64px)`,
        display: 'inline-block !important',
        paddingTop: '0.5rem',
        padding: 0,
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 12rem)`,
            zIndex: 10,
            marginRight: theme.spacing(0),
            marginLeft: theme.spacing(0),
        },
    },
    tabButton: {
        borderRadius: '30px',
        textTransform: 'capitalize',
        fontWeight: 'normal',
    },
    buttonPanel: {
        marginTop: theme.spacing(4),
        backgroundColor: grey[100],
    },
    recordList: {
        "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
            display: "none",
        },
        "& .MuiDataGrid-virtualScroller": {
            marginTop: "0 !important",
        },
    }
}));

const validateInput = (cat) => {
    const errors = {};
    let canSave = true;
    if (!cat.name || cat.name.length <= 0) {
        if (cat._changed && cat.name_blured) {
            errors.name = { error: true, helperText: 'Name is required' };
        }
        canSave = false;
    }
    if (!cat.rendering_type) {
        if (cat._changed) {
            errors.rendering_type = { error: true, helperText: 'Rendering type is required' };
        }
        canSave = false;
    }
    return { canSave, errors };
};

const getComparableState = (item_category) => {
    const a = {
        ...item_category,
    };
    delete a._changed;
    delete a._blured;
    return JSON.stringify(a);
};

const PreTripCategoryEdit = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm, currentLicensee } = React.useContext(ContextStore);
    const history = useHistory();
    const resource = 'pre_trip_item_type';
    // const history = useHistory();
    const classes = useStyles();
    const [syncActive, setSyncActive] = React.useState(false);
    const [, setServerErrors] = React.useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const [cat, setCat] = React.useState(() => {
        const data = props.data;
        // Default tour_operator to current licensee for new categories in franchise mode
        if (!data.id && !data.tour_operator && currentLicensee) {
            return { ...data, tour_operator: currentLicensee };
        }
        return data;
    });
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const isSmallScreen = !useIsWidthUp('sm')
    const hasWritePerm = hasPerm('pre_trip_item', 'write')

    const onLicenseeChange = (id) => {
        setCat((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    const urlParams = useParams();
    const [currentTab, setCurrentTab] = React.useState(0);

    const handleTabChange = (ev, newTab) => {
        setCurrentTab(newTab);
    };

    if (cat.id !== props.data.id) {
        setCat(props.data);
    }



    const originalState = getComparableState(props.data);
    const currentState = getComparableState(cat);
    const isChanged = originalState !== currentState;
    const { canSave, errors } = validateInput(cat);


    const handleChange = (ev) => {
        if (ev.target.name === 'rendering_type') {
            setCat((prevState) => ({ ...prevState, [ev.target.name]: parseInt(ev.target.value), _changed: true }));
        } else {
            setCat((prevState) => ({ ...prevState, [ev.target.name]: ev.target.value, _changed: true }));
        }
        props.setDetailsDirty && props.setDetailsDirty(true);
    };
    const handleSave = (ev) => {
        console.log('save', cat);
        setSyncActive(true);
        (async () => {
            try {
                const ACTION = cat.id ? UPDATE : CREATE;
                const res = await dataAPI(ACTION, resource, { id: cat.id, data: cat });
                console.log('update', res);
                if (res.statusCode === 200) {
                    enqueueSnackbar('Trip item category updated', { variant: 'success' });
                    props.onChange(res.data);
                    setCat((prevState) => ({ ...prevState, _changed: false }));
                    props.setDetailsDirty && props.setDetailsDirty(false);
                } else if (res.statusCode === 201) {
                    enqueueSnackbar('Trip item category created', { variant: 'success' });
                    props.onChange(res.data);
                    setCat((prevState) => ({ ...prevState, _changed: false }));
                    props.setDetailsDirty && props.setDetailsDirty(false);
                } else if (res.statusCode === 406) {
                    enqueueSnackbar('Trip item category validation error', { variant: 'warning' });
                    console.log('validation errors', res.data);
                    setServerErrors(res.data.errors);
                }
            } finally {
                setSyncActive(false);
            }
        })();
    };

    const doDelete = (ev) => {
        console.log('deleting', props.data.id);
        props.onDelete && props.onDelete(props.data);
    };

    const handleDelete = (ev) => {
        setConfirmationDialogAction('delete_pretrip_category');
    };


    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_pretrip_category':
            case 'delete_pretrip_categories':
                doDelete();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const onBlur = (ev) => {
        setCat((prevState) => ({ ...prevState, [ev.target.name + '_blured']: true }));
    };

    const onDeleteItem = async (item) => {
        await props.setDeleteItemFromCategoryEditPage(item.id)
        let delete_from_edit_category_page = true
        props.onDeleteItemList([item.id], delete_from_edit_category_page)
    }

    const onRedirectToItem = async (item) => {
        props.handleTabChange({}, 0)
        history.push(`/pretrip/items/${item.id}`)
    }

    if (!hasPerm('pre_trip_item', 'read')) {
        return <AccessDeniedView />
    }

    if (!cat.id && urlParams.id) {
        return <HTTPError resource={resource} id={props.data?.id || props.catId} code={404} />
    }

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(cat.tour_operator)}>
        <div className={classes.root}>
            <PageMeta title={`Edit Trip Item Category - ${cat.name}`} />
            <Prompt when={!!cat._changed && hasPerm('pre_trip_item', 'write')} message="Are you sure you want to leave without saving changes?" />

            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            <Grid container direction="row" >
                <Grid item xs={12} container alignContent="center" style={{ backgroundColor: '#F5F5F5', height: "64px", zIndex: 100 }}>
                    <Grid item xs={8} mt={0.25} pl={2}>
                        <Typography variant="h6">{cat.id ? 'Category Details' : 'Create New Category'}</Typography>
                    </Grid>
                    <Grid item xs={4} container justifyContent="flex-end"  >
                        <Tooltip title="Close">
                            <Button aria-label="close-preview" onClick={props.onClose}>
                                <CloseIcon style={{ color: 'black' }} />
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>
                {cat.id && <Grid item xs={12}>
                    <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                        <Tab label="General" {...a11yProps(0)} />
                        <Tab label="Used by" {...a11yProps(1)} />
                    </Tabs>
                </Grid>}
                {currentTab === 0 && <Grid item xs={12} container direction="row" justifyContent="flex-start" p={1} spacing={2} className={classes.gridRoot} pt={3}>
                    <Grid item xs={12}>
                        <LicenseeFormField value={cat.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                    </Grid>
                    <Grid item xs={12} >
                        <TextField
                            disabled={!hasWritePerm}
                            helperText={`${64 - (cat.name || '').length} characters left`}
                            {...errors.name}
                            required
                            variant="filled"
                            size="small"
                            fullWidth
                            name="name"
                            label="Display name"
                            value={cat.name || ''}
                            onChange={handleChange}
                            inputProps={{ maxLength: 64 }}
                            onBlur={onBlur}
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <TextField
                            disabled={!hasWritePerm}
                            helperText={`${64 - (cat.system_name || '').length} characters left`}
                            {...errors.system_name}
                            required
                            variant="filled"
                            size="small"
                            fullWidth
                            name="system_name"
                            label="System name"
                            value={cat.system_name || ''}
                            onChange={handleChange}
                            inputProps={{ maxLength: 64 }}
                            onBlur={onBlur}
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <FormControl variant="filled" size="small" fullWidth className={classes.formControl} disabled={!hasWritePerm}>
                            <InputLabel htmlFor="rendering-type-select-element">Type:</InputLabel>
                            <Select value={cat.rendering_type} onChange={handleChange} inputProps={{ name: 'rendering_type', id: 'rendering-type-select-element' }}>
                                {renderingTypesMenuItems}
                            </Select>
                        </FormControl>
                    </Grid>
                    {cat.id && <Grid item xs={12}>
                        <Typography fontSize={18} fontWeight={600} mb={2}>Items in this category ({cat.count})</Typography>
                        <Grid style={{ height: isSmallScreen ? `calc(100vh - 32rem)` : `calc(100vh - 41rem)`, minHeight: '300px' }}>
                            <RecordList
                                className={classes.recordList}
                                resource={'pre_trip_item'}
                                noBorder
                                apiFilters={[{ field: "item_type", value: [cat.id] }]}
                                checkboxSelection={false}
                                disableSelectionOnClick={true}
                                disableColumnFilter={true}
                                disableListBorder={true}
                                headerVisibility={2}
                                columns={[
                                    { field: 'name', headerName: 'Name', sortable: false, flex: 1 },
                                    {
                                        field: 'actions',
                                        headerName: ' ',
                                        sortable: false,
                                        width: 100,
                                        valueGetter: (p) => p.row,
                                        renderCell: (row) => (
                                            <Grid container justifyContent="flex-end">
                                                <Grid item xs={'auto'} >
                                                    <Tooltip title="Edit trip item">
                                                        <IconButton aria-label="edit" color="primary" onClick={() => onRedirectToItem(row)}>
                                                            <EditTripItemIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid item xs={'auto'}>
                                                    <Tooltip title="Delete trip item">
                                                        <span>
                                                            <IconButton disabled={!hasWritePerm} aria-label="delete" color="primary" onClick={() => onDeleteItem(row)}>
                                                                <CloseIcon style={{ fontSize: 23 }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                        )

                                    }
                                ]}
                            />
                        </Grid>
                    </Grid>}
                </Grid>}
                {cat.id && currentTab === 1 && <Grid item xs={12} p={1} className={classes.gridRoot} style={{ overflow: 'auto' }}>
                    <UsedByPanel resource={resource} id={cat.id} layout="narrow" />
                </Grid>}
            </Grid>

            <Box p={1} className={classes.buttonPanel}>
                <FormActionButtons
                    mode={cat.id ? 'edit' : 'create'}
                    onSubmit={handleSave}
                    submitDisabled={!hasPerm('pre_trip_item', 'write') || !canSave || !isChanged}
                    onCancel={props.onClose}
                    onDelete={cat.id ? handleDelete : undefined}
                    deletePerm="pre_trip_item.delete"
                />
            </Box>
        </div>
        </FormLicenseeContext.Provider>
    );
};

export default PreTripCategoryEdit;
